import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  TrendUp, TrendDown, Warning, Package, ShoppingCart, ArrowRight, Skull, Clock
} from "@phosphor-icons/react";

const URGENCY_TONE = {
  critical: "bg-red-100 text-red-700 hover:bg-red-100",
  high:     "bg-amber-100 text-amber-700 hover:bg-amber-100",
  normal:   "bg-muted text-muted-foreground",
};

function Row({ children, testId }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm" data-testid={testId}>
      {children}
    </div>
  );
}

export default function SmartInventoryPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/inventory/analysis").then(r => setD(r.data)).catch(()=>{}); }, []);
  if (!d) return null;

  const empty = (msg) => <div className="text-xs text-muted-foreground italic p-3">{msg}</div>;

  return (
    <Card className="shadow-none" data-testid="smart-inventory-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold text-lg tracking-tight">Smart Inventory</div>
          <div className="flex items-center gap-1.5 text-[10px] flex-wrap">
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="inv-cnt-fast">
              <TrendUp size={10} weight="bold" className="mr-0.5"/>Fast · {d.counts.fast_movers}
            </Badge>
            <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">
              <TrendDown size={10} weight="bold" className="mr-0.5"/>Slow · {d.counts.slow_movers}
            </Badge>
            <Badge variant="secondary"><Skull size={10} weight="bold" className="mr-0.5"/>Dead · {d.counts.dead}</Badge>
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
              <Clock size={10} className="mr-0.5"/>Near expiry · {d.counts.near_expiry}
            </Badge>
            <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><Warning size={10} className="mr-0.5"/>Below min · {d.counts.below_min}</Badge>
          </div>
        </div>

        <Tabs defaultValue="purchase">
          <TabsList>
            <TabsTrigger value="purchase" data-testid="inv-tab-purchase"><ShoppingCart size={12} className="mr-1"/>Purchase suggestions ({d.counts.purchase_suggestions})</TabsTrigger>
            <TabsTrigger value="belowmin" data-testid="inv-tab-belowmin">Below reorder ({d.counts.below_min})</TabsTrigger>
            <TabsTrigger value="fast"><TrendUp size={12} className="mr-1"/>Fast movers</TabsTrigger>
            <TabsTrigger value="slow"><TrendDown size={12} className="mr-1"/>Slow / dead</TabsTrigger>
            <TabsTrigger value="expiry" data-testid="inv-tab-expiry"><Clock size={12} className="mr-1"/>Near expiry</TabsTrigger>
          </TabsList>

          <TabsContent value="purchase" className="mt-3">
            {d.purchase_suggestions.length === 0 ? empty("No purchase needed — stocks look healthy.") : (
              <div className="space-y-1.5 max-h-72 overflow-auto">
                {d.purchase_suggestions.map(p => (
                  <Row key={p.material_id} testId={`inv-purchase-${p.material_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{p.name}</div>
                      <div className="text-[10px] text-muted-foreground">{p.category} · {p.supplier || "no supplier set"}</div>
                    </div>
                    <div className="text-right flex items-center gap-3 ml-3">
                      <div className="text-right">
                        <div className="mono text-xs font-bold">Need <span className="text-red-600">{p.shortfall}</span> {p.unit}</div>
                        <div className="mono text-[10px] text-muted-foreground">stock {p.current_stock} / min {p.min_stock}</div>
                      </div>
                      <Badge className={URGENCY_TONE[p.urgency]}>{p.urgency}</Badge>
                    </div>
                  </Row>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="belowmin" className="mt-3">
            {d.below_min.length === 0 ? empty("All SKUs above reorder point.") : (
              <div className="space-y-1.5 max-h-72 overflow-auto">
                {d.below_min.map(r => (
                  <Row key={r.sku_id} testId={`inv-belowmin-${r.sku_code}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{r.sku_name}</div>
                      <div className="mono text-[10px] text-muted-foreground">{r.sku_code} · {r.category}</div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="mono text-xs font-bold text-red-600">{r.stock}<span className="text-muted-foreground"> / {r.min_stock}</span></div>
                      <div className="mono text-[10px] text-muted-foreground">
                        {r.days_of_cover !== null ? `${r.days_of_cover}d cover` : `${r.velocity_30d}/d`}
                      </div>
                    </div>
                  </Row>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="fast" className="mt-3">
            {d.fast_movers.length === 0 ? empty("No fast movers yet — sales history too thin.") : (
              <div className="space-y-1.5 max-h-72 overflow-auto">
                {d.fast_movers.map(r => (
                  <Row key={r.sku_id}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{r.sku_name}</div>
                      <div className="mono text-[10px] text-muted-foreground">{r.sku_code} · {r.category}</div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="mono text-xs font-bold text-emerald-600">{r.velocity_30d}/d</div>
                      <div className="mono text-[10px] text-muted-foreground">stock {r.stock}</div>
                    </div>
                  </Row>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="slow" className="mt-3">
            {d.dead.length + d.slow_movers.length === 0 ? empty("No slow or dead inventory.") : (
              <div className="space-y-1.5 max-h-72 overflow-auto">
                {d.dead.map(r => (
                  <Row key={r.sku_id}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{r.sku_name}</div>
                      <div className="mono text-[10px] text-muted-foreground">{r.sku_code} · {r.category}</div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Badge variant="secondary">DEAD</Badge>
                      <div className="mono text-xs font-bold">{r.stock}</div>
                    </div>
                  </Row>
                ))}
                {d.slow_movers.map(r => (
                  <Row key={"s"+r.sku_id}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{r.sku_name}</div>
                      <div className="mono text-[10px] text-muted-foreground">{r.sku_code} · {r.category}</div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="mono text-xs font-bold text-sky-600">{r.velocity_30d}/d</div>
                      <div className="mono text-[10px] text-muted-foreground">vs {r.velocity_90d}/d 90d</div>
                    </div>
                  </Row>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="expiry" className="mt-3">
            {d.near_expiry.length === 0 ? empty("No stock expiring in the next 30 days.") : (
              <div className="space-y-1.5 max-h-72 overflow-auto">
                {d.near_expiry.map((r, i) => (
                  <Row key={i}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{r.sku_name} <span className="mono text-[10px] text-muted-foreground">· {r.batch_no}</span></div>
                      <div className="mono text-[10px] text-muted-foreground">Expires {new Date(r.expiry_date).toLocaleDateString()}</div>
                    </div>
                    <Badge className={r.critical ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>
                      {r.days_left} days · {r.qty} pouches
                    </Badge>
                  </Row>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
